(() => {
  const API_URL = 'https://shipping.miixpro.com/api/query';
  const TRACKING_URL = 'http://szjhy.rtb56.com/track_query.aspx';
  const NO_MATCH_MESSAGE = 'Order details not recognized. Please check both your email address and PBID.';

  const isTrustedTrackingUrl = (value) => {
    try {
      const url = new URL(String(value || ''));
      const code = url.searchParams.get('c') || '';
      return url.protocol === 'http:' &&
        url.hostname === 'szjhy.rtb56.com' &&
        url.pathname === '/track_query.aspx' &&
        /^[a-f0-9]{32}$/i.test(code) &&
        Array.from(url.searchParams.keys()).every((key) => key === 'c');
    } catch (_) {
      return false;
    }
  };

  const copyText = async (value) => {
    const text = String(value || '');
    if (!text) return false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (_) {}

    const input = document.createElement('textarea');
    input.value = text;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.append(input);
    input.select();
    let copied = false;
    try { copied = document.execCommand('copy'); } catch (_) { copied = false; }
    input.remove();
    return copied;
  };

  const makeElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };

  const addDetail = (list, label, value) => {
    const item = document.createElement('div');
    item.append(makeElement('dt', '', label), makeElement('dd', '', value));
    list.append(item);
  };

  const showResult = (container, state) => {
    container.replaceChildren();
    container.hidden = false;
    container.className = `shipping-status-result ${state.className || ''}`.trim();

    const icon = makeElement('span', 'shipping-result-icon', state.icon || 'i');
    icon.setAttribute('aria-hidden', 'true');
    const main = makeElement('div', 'shipping-result-main');
    main.append(makeElement('h2', '', state.heading));

    if (state.message) main.append(makeElement('p', '', state.message));
    if (state.backerNumber) {
      const details = makeElement('dl', 'shipping-result-details');
      addDetail(details, 'Backer Number', state.backerNumber);
      if (state.trackingNumber) addDetail(details, 'Tracking Number', state.trackingNumber);
      main.append(details);
    }
    if (state.trackingNumber) {
      const directUrl = isTrustedTrackingUrl(state.trackUrl) ? state.trackUrl : '';
      const actions = makeElement('div', 'shipping-track-actions');
      const link = makeElement('a', 'shipping-track-link', directUrl ? 'View logistics tracking events' : 'Open logistics tracking page');
      link.href = directUrl || TRACKING_URL;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      const copyButton = makeElement('button', 'shipping-copy-button', 'Copy tracking number');
      copyButton.type = 'button';
      const copyStatus = makeElement('span', 'shipping-copy-status');
      copyStatus.setAttribute('role', 'status');
      copyStatus.setAttribute('aria-live', 'polite');

      const reportCopyResult = async () => {
        const copied = await copyText(state.trackingNumber);
        copyStatus.textContent = copied
          ? 'Tracking number copied.'
          : 'Copy was blocked. Select the tracking number above and copy it manually.';
      };

      copyButton.addEventListener('click', reportCopyResult);
      if (!directUrl) {
        link.addEventListener('click', () => {
          void reportCopyResult();
        });
      }
      actions.append(link, copyButton, copyStatus);
      main.append(actions);
    }
    container.append(icon, main);
  };

  const initialiseShipping = (root) => {
    const form = root.querySelector('#shipping-status-form');
    const result = root.querySelector('#shipping-status-result');
    if (!form || !result || form.dataset.initialised === 'true') return;
    form.dataset.initialised = 'true';

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const emailInput = form.elements.namedItem('email');
      const pbidInput = form.elements.namedItem('pbid');
      const submit = form.querySelector('button[type="submit"]');
      const email = String(emailInput?.value || '').trim().toLowerCase();
      const pbid = String(pbidInput?.value || '').trim().toUpperCase();

      if (!email || email.length > 254 || !emailInput.checkValidity() || !pbid || pbid.length > 32) {
        showResult(result, { className: 'is-error', icon: '!', heading: 'Unable to check shipping status', message: NO_MATCH_MESSAGE });
        return;
      }

      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      showResult(result, { className: 'is-loading', icon: '…', heading: 'Checking shipping status' });
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 15000);

      try {
        const response = await fetch(API_URL, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          referrerPolicy: 'strict-origin-when-cross-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, pbid }),
          signal: controller.signal
        });
        let payload = null;
        try { payload = await response.json(); } catch (_) { payload = null; }

        if (response.status === 429) {
          showResult(result, { className: 'is-error', icon: '!', heading: 'Unable to check shipping status', message: 'Too many attempts. Please wait a few minutes and try again.' });
        } else if (!response.ok || !payload || payload.ok !== true) {
          showResult(result, { className: 'is-error', icon: '!', heading: 'No matching order', message: NO_MATCH_MESSAGE });
        } else if (payload.status === 'shipped' && payload.backerNumber && payload.trackingNumber) {
          showResult(result, { className: 'is-shipped', icon: '✓', heading: 'Shipping Status: Shipped', backerNumber: String(payload.backerNumber), trackingNumber: String(payload.trackingNumber), trackUrl: String(payload.trackUrl || '') });
        } else if (payload.status === 'pending' && payload.backerNumber) {
          showResult(result, { className: 'is-pending', icon: '•', heading: 'Shipping Status: Not Shipped', backerNumber: String(payload.backerNumber) });
        } else {
          showResult(result, { className: 'is-error', icon: '!', heading: 'No matching order', message: NO_MATCH_MESSAGE });
        }
      } catch (_) {
        showResult(result, { className: 'is-error', icon: '!', heading: 'Network error', message: 'We could not check your shipping status. Please try again later.' });
      } finally {
        window.clearTimeout(timeout);
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
      }
    });
  };

  const updateManualPageNavigation = () => {
    document.querySelectorAll('a[href]').forEach((link) => {
      let url;
      try { url = new URL(link.href, window.location.origin); } catch (_) { return; }

      const label = String(link.textContent || '').trim().toLowerCase().replace(/\s+/g, ' ');
      if (url.hostname === 'htu.miixpro.com' && label === 'user manual') {
        link.href = '/pages/user-manual';
      } else if (url.hostname === 'htu.miixpro.com' && (label === 'firmware update' || label === 'firmware updates')) {
        link.href = '/pages/firmware-updates';
      } else if (url.origin === window.location.origin && url.pathname === '/pages/ks-tracking') {
        link.href = '/pages/shipping-status';
        if (label === 'track kickstarter order' || label === 'track ks order') link.textContent = 'Shipping Status';
      }
    });
  };

  const initialiseManual = (root) => {
    if (root.dataset.initialised === 'true') return;
    root.dataset.initialised = 'true';
    const openButton = root.querySelector('.manual-menu-button');
    const sidebar = root.querySelector('.sidebar');
    const backdrop = root.querySelector('.manual-sidebar-backdrop');
    const closeButtons = root.querySelectorAll('[data-manual-sidebar-close]');
    const firmwareButton = root.querySelector('.sidebar-firmware-toggle');
    const firmwareBody = root.querySelector('.sidebar-firmware-body');

    const setSidebar = (open) => {
      root.classList.toggle('is-sidebar-open', open);
      openButton?.setAttribute('aria-expanded', String(open));
      if (backdrop) backdrop.hidden = !open;
      document.documentElement.classList.toggle('miixkey-manual-lock', open);
      if (open) sidebar?.querySelector('.manual-sidebar-close')?.focus();
    };
    openButton?.addEventListener('click', () => setSidebar(true));
    closeButtons.forEach((button) => button.addEventListener('click', () => setSidebar(false)));
    sidebar?.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setSidebar(false)));

    firmwareButton?.addEventListener('click', () => {
      const expanded = firmwareButton.getAttribute('aria-expanded') !== 'false';
      firmwareButton.setAttribute('aria-expanded', String(!expanded));
      firmwareBody?.classList.toggle('is-collapsed', expanded);
    });

    const overlay = root.querySelector('[data-manual-lightbox]');
    const overlayImage = root.querySelector('[data-manual-lightbox-image]');
    const closeLightbox = () => {
      overlay?.classList.remove('show');
      overlay?.setAttribute('aria-hidden', 'true');
      if (overlayImage) overlayImage.removeAttribute('src');
    };
    root.querySelectorAll('.prose img').forEach((image) => {
      image.addEventListener('click', () => {
        if (image.closest('a') || !overlay || !overlayImage) return;
        overlayImage.src = image.currentSrc || image.src;
        overlayImage.alt = image.alt || '';
        overlay.classList.add('show');
        overlay.setAttribute('aria-hidden', 'false');
        overlay.querySelector('.lightbox-close')?.focus();
      });
    });
    overlay?.addEventListener('click', (event) => { if (event.target !== overlayImage) closeLightbox(); });
    overlay?.querySelector('.lightbox-close')?.addEventListener('click', closeLightbox);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        if (overlay?.classList.contains('show')) closeLightbox();
        else if (root.classList.contains('is-sidebar-open')) setSidebar(false);
      }
    });

    updateManualPageNavigation();
    initialiseShipping(root);
  };

  const initialiseAll = () => document.querySelectorAll('[data-miixkey-manual]').forEach(initialiseManual);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialiseAll, { once: true });
  else initialiseAll();
  document.addEventListener('shopify:section:load', initialiseAll);
})();

